import { Link, useNavigate } from "react-router-dom";
import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";
import { ShoppingBag, Menu, X, Sparkles, User, LogOut, LayoutDashboard } from "lucide-react";
import { Button } from "@/components/ui/button";

const NAV = [
  { label: "Shop", to: "/shop" },
  { label: "Faith Collection", to: "/faith" },
  { label: "Instant Downloads", to: "/shop?instant=true" },
  { label: "How It Works", to: "/how-it-works" },
  { label: "Free Preview", to: "/free-preview" },
  { label: "Blog", to: "/blog" },
];

export default function Header() {
  const { user, logout } = useAuth();
  const { count } = useCart();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  const doLogout = async () => { await logout(); navigate("/"); };

  return (
    <header className="glass sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-4">
        <Link to="/" data-testid="logo-link" className="flex items-center gap-2 shrink-0">
          <span className="w-9 h-9 rounded-xl bg-amber flex items-center justify-center text-white">
            <Sparkles className="w-5 h-5" />
          </span>
          <span className="font-display text-lg sm:text-xl font-bold text-slateink leading-none">
            Made For Me<span className="text-amber"> Studio</span>
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-7">
          {NAV.map((n) => (
            <Link key={n.label} to={n.to} data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
              className="text-sm font-medium text-slate-600 hover:text-amber transition-colors">
              {n.label}
            </Link>
          ))}
        </nav>

        <div className="flex items-center gap-2 sm:gap-3">
          <Link to="/cart" data-testid="cart-link" className="relative p-2 text-slate-700 hover:text-amber transition-colors">
            <ShoppingBag className="w-5 h-5" />
            {count > 0 && (
              <span data-testid="cart-count" className="absolute -top-0.5 -right-0.5 bg-rose text-white text-[10px] font-bold w-4 h-4 rounded-full flex items-center justify-center">{count}</span>
            )}
          </Link>

          {user ? (
            <div className="hidden sm:flex items-center gap-2">
              {user.role === "admin" && (
                <Button asChild variant="ghost" size="sm" data-testid="admin-nav-btn" className="text-slate-700">
                  <Link to="/admin"><LayoutDashboard className="w-4 h-4 mr-1" />Admin</Link>
                </Button>
              )}
              <Button asChild variant="ghost" size="sm" data-testid="account-nav-btn" className="text-slate-700">
                <Link to="/account"><User className="w-4 h-4 mr-1" />Account</Link>
              </Button>
              <Button onClick={doLogout} variant="ghost" size="sm" data-testid="logout-btn" className="text-slate-500">
                <LogOut className="w-4 h-4" />
              </Button>
            </div>
          ) : (
            <Button asChild variant="ghost" size="sm" data-testid="login-nav-btn" className="hidden sm:inline-flex text-slate-700">
              <Link to="/login">Sign in</Link>
            </Button>
          )}

          <Button asChild data-testid="header-create-btn" className="hidden sm:inline-flex rounded-full bg-amber hover:bg-amber-600 text-white font-semibold">
            <Link to="/shop">Create My Book</Link>
          </Button>

          <button data-testid="mobile-menu-btn" className="lg:hidden p-2 text-slateink" onClick={() => setOpen(!open)}>
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden border-t border-slate-100 bg-white px-4 py-4 space-y-1" data-testid="mobile-menu">
          {NAV.map((n) => (
            <Link key={n.label} to={n.to} onClick={() => setOpen(false)}
              className="block py-2.5 text-slate-700 font-medium">{n.label}</Link>
          ))}
          <div className="pt-3 mt-2 border-t border-slate-100 flex flex-col gap-2">
            {user ? (
              <>
                {user.role === "admin" && <Link to="/admin" onClick={() => setOpen(false)} className="py-2 text-slate-700 font-medium">Admin Dashboard</Link>}
                <Link to="/account" onClick={() => setOpen(false)} className="py-2 text-slate-700 font-medium">My Account</Link>
                <button onClick={() => { setOpen(false); doLogout(); }} className="py-2 text-left text-slate-500 font-medium">Sign out</button>
              </>
            ) : (
              <Link to="/login" onClick={() => setOpen(false)} className="py-2 text-slate-700 font-medium">Sign in</Link>
            )}
            <Button asChild className="rounded-full bg-amber hover:bg-amber-600 text-white font-semibold mt-1">
              <Link to="/shop" onClick={() => setOpen(false)}>Create My Book</Link>
            </Button>
          </div>
        </div>
      )}
    </header>
  );
}
